import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { X, Save, Plus, Trash2, FileText, Loader2 } from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import PatientSelector from "@/components/clinical-notes/PatientSelector";

export default function InvoiceForm({
  invoice,
  onSave,
  onClose,
  isLoading = false,
}) {
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      patientId: "",
      patientEmail: "",
      patientName: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      dueDate: new Date().toISOString().split("T")[0],
      lineItems: [],
      notes: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "lineItems",
  });

  // Fetch patients for the searchable dropdown
  const { data: patients = [] } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get(ENDPOINTS.PATIENTS.LIST),
  });

  // Fetch services for line item selection
  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: () => apiClient.get(ENDPOINTS.SERVICES.LIST),
  });

  // Watch values for computed fields
  const lineItems = watch("lineItems");
  const patientId = watch("patientId");

  // Load existing invoice data when editing
  useEffect(() => {
    if (invoice) {
      reset({
        patientId: invoice.patientId || "",
        patientEmail: invoice.patientEmail || "",
        patientName: invoice.patientName || "",
        invoiceDate: invoice.invoiceDate
          ? new Date(invoice.invoiceDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        dueDate: invoice.dueDate
          ? new Date(invoice.dueDate).toISOString().split("T")[0]
          : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0],
        lineItems: invoice.lineItems || [],
        notes: invoice.notes || "",
      });
    }
  }, [invoice, reset]);

  // Update patient details when patient is selected
  useEffect(() => {
    if (patientId && patients.length > 0) {
      const selectedPatient = patients.find((p) => p.id === patientId);
      if (selectedPatient) {
        setValue("patientEmail", selectedPatient.email || "");
        setValue("patientName", selectedPatient.name || "");
      }
    }
  }, [patientId, patients, setValue]);

  const addLineItem = () => {
    append({
      itemType: "service",
      itemId: "",
      description: "",
      quantity: 1,
      unitPrice: 0,
      lineAmount: 0,
      accountCode: "200",
      taxType: "OUTPUT3",
    });
  };

  // Handle item type change - clear fields when switching
  const handleItemTypeChange = (index, value) => {
    setValue(`lineItems.${index}.itemType`, value);
    setValue(`lineItems.${index}.itemId`, "");
    setValue(`lineItems.${index}.description`, "");
    setValue(`lineItems.${index}.unitPrice`, 0);
    setValue(`lineItems.${index}.lineAmount`, 0);
  };

  // Handle service selection - auto-populate fields
  const handleServiceChange = (index, serviceId) => {
    setValue(`lineItems.${index}.itemId`, serviceId);
    const service = services.find((s) => s.id === serviceId);
    if (service) {
      const quantity = lineItems[index]?.quantity || 1;
      setValue(`lineItems.${index}.description`, service.name);
      setValue(`lineItems.${index}.unitPrice`, service.basePrice || 0);
      setValue(`lineItems.${index}.lineAmount`, quantity * (service.basePrice || 0));
    }
  };

  // Handle quantity change - recalculate line amount
  const handleQuantityChange = (index, quantity) => {
    const parsedQuantity = parseInt(quantity) || 1;
    setValue(`lineItems.${index}.quantity`, parsedQuantity);
    const unitPrice = lineItems[index]?.unitPrice || 0;
    setValue(`lineItems.${index}.lineAmount`, parsedQuantity * unitPrice);
  };

  // Handle unit price change - recalculate line amount
  const handleUnitPriceChange = (index, unitPrice) => {
    const parsedPrice = parseFloat(unitPrice) || 0;
    setValue(`lineItems.${index}.unitPrice`, parsedPrice);
    const quantity = lineItems[index]?.quantity || 1;
    setValue(`lineItems.${index}.lineAmount`, quantity * parsedPrice);
  };

  const calculateTotals = () => {
    const subtotal = (lineItems || []).reduce(
      (sum, item) => sum + (item?.lineAmount || 0),
      0
    );
    const taxRate = 0.15;
    const taxAmount = subtotal * taxRate;
    const total = subtotal + taxAmount;

    return { subtotal, taxRate, taxAmount, total };
  };

  const onSubmit = (data) => {
    const { subtotal, taxRate, taxAmount, total } = calculateTotals();

    const invoiceData = {
      patientId: data.patientId,
      patientEmail: data.patientEmail,
      patientName: data.patientName,
      invoiceDate: data.invoiceDate,
      dueDate: data.dueDate,
      lineItems: data.lineItems,
      subtotal,
      taxRate,
      taxAmount,
      total,
      notes: data.notes,
      status: "draft",
    };

    onSave(invoiceData);
  };

  const { subtotal, taxRate, taxAmount, total } = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <CardTitle>{invoice ? "Edit Invoice" : "New Invoice"}</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Patient Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="patientId"
                control={control}
                rules={{ required: "Patient is required" }}
                render={({ field }) => (
                  <PatientSelector
                    patients={patients}
                    selectedPatient={field.value}
                    onPatientChange={field.onChange}
                    label="Patient *"
                    placeholder="Select patient..."
                    showAll={false}
                    disabled={!!invoice}
                    error={errors.patientId?.message}
                  />
                )}
              />
              <div className="space-y-2">
                <Label htmlFor="patientEmail">Patient Email *</Label>
                <Input
                  id="patientEmail"
                  {...register("patientEmail", { required: "Email is required" })}
                  placeholder="patient@example.com"
                  disabled={!!invoice}
                  className={errors.patientEmail ? "border-red-500" : ""}
                />
                {errors.patientEmail && (
                  <p className="text-sm text-red-600">{errors.patientEmail.message}</p>
                )}
              </div>
            </div>

            {/* Invoice Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invoiceDate">Invoice Date *</Label>
                <Input
                  id="invoiceDate"
                  type="date"
                  {...register("invoiceDate", { required: "Invoice date is required" })}
                  className={errors.invoiceDate ? "border-red-500" : ""}
                />
                {errors.invoiceDate && (
                  <p className="text-sm text-red-600">{errors.invoiceDate.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="dueDate">Due Date *</Label>
                <Input
                  id="dueDate"
                  type="date"
                  {...register("dueDate", { required: "Due date is required" })}
                  className={errors.dueDate ? "border-red-500" : ""}
                />
                {errors.dueDate && (
                  <p className="text-sm text-red-600">{errors.dueDate.message}</p>
                )}
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-slate-900">
                  Line Items
                </h3>
                <Button
                  type="button"
                  onClick={addLineItem}
                  size="sm"
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>

              {fields.length === 0 ? (
                <div className="p-8 text-center text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No line items yet. Click "Add Item" to get started.</p>
                </div>
              ) : (
                fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="p-4 border border-slate-200 rounded-lg bg-slate-50/60"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-slate-800">
                        Item #{index + 1}
                      </h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {/* Item Type */}
                      <div className="space-y-2">
                        <Label>Item Type *</Label>
                        <Controller
                          name={`lineItems.${index}.itemType`}
                          control={control}
                          render={({ field: itemTypeField }) => (
                            <Select
                              value={itemTypeField.value}
                              onValueChange={(value) => handleItemTypeChange(index, value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="service">Service</SelectItem>
                                <SelectItem value="fee">Custom Fee</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <p className="text-xs text-amber-600">
                          Medications can only be invoiced through the
                          Prescription flow (Dispense → Generate Invoice)
                        </p>
                      </div>

                      {/* Service Selection */}
                      {lineItems[index]?.itemType === "service" && (
                        <div className="space-y-2">
                          <Label>Service *</Label>
                          <Controller
                            name={`lineItems.${index}.itemId`}
                            control={control}
                            render={({ field: serviceField }) => (
                              <Select
                                value={serviceField.value}
                                onValueChange={(value) => handleServiceChange(index, value)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select service..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {services.map((service) => (
                                    <SelectItem key={service.id} value={service.id}>
                                      <div className="flex flex-col">
                                        <span className="font-medium">
                                          {service.name}
                                        </span>
                                        <span className="text-sm text-slate-500">
                                          R {service.basePrice?.toFixed(2)} •{" "}
                                          {service.category}
                                        </span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>
                      )}

                      {/* Description, Quantity, Price */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2 md:col-span-2">
                          <Label>Description *</Label>
                          <Input
                            {...register(`lineItems.${index}.description`, {
                              required: "Description is required",
                            })}
                            placeholder="Item description"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Quantity *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={lineItems[index]?.quantity || 1}
                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Unit Price (R) *</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={lineItems[index]?.unitPrice || 0}
                            onChange={(e) => handleUnitPriceChange(index, e.target.value)}
                            disabled={lineItems[index]?.itemType === "service"}
                            className={lineItems[index]?.itemType === "service" ? "bg-slate-100" : ""}
                          />
                          {lineItems[index]?.itemType === "service" && (
                            <p className="text-xs text-slate-500">
                              Price is set by the selected service
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Line Amount (R)</Label>
                          <Input
                            type="number"
                            value={(lineItems[index]?.lineAmount || 0).toFixed(2)}
                            disabled
                            className="bg-slate-100"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Totals */}
            {fields.length > 0 && (
              <div className="border-t border-slate-200 pt-4">
                <div className="max-w-md ml-auto space-y-2">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal:</span>
                    <span className="font-medium">R {subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>VAT ({(taxRate * 100).toFixed(0)}%):</span>
                    <span className="font-medium">
                      R {taxAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t border-slate-200">
                    <span>Total:</span>
                    <span>R {total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="Additional notes or comments..."
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                disabled={
                  fields.length === 0 ||
                  !watch("patientId") ||
                  isLoading
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {invoice ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {invoice ? "Update Invoice" : "Create Invoice"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
